import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Box, Checkbox, Flex } from '@mantine/core';
import type { Column } from '@tanstack/react-table';
import { DragDropIcon } from 'icons';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DropIndicator } from './DropIndicator';
import { getColumnData, isColumnData } from './helpers';

type ColumnState =
  | {
      type: 'idle';
    }
  | {
      type: 'preview';
      container: HTMLElement;
    }
  | {
      type: 'is-dragging';
    }
  | {
      type: 'is-dragging-over';
      closestEdge: Edge | null;
    };

const idle: ColumnState = { type: 'idle' };

export type ColumnItemProps = {
  column: Column<any, unknown>;
  // header: Header<any, unknown>;
};

const getLabel = (column: Column<any, unknown>): ReactNode => {
  const { header } = column.columnDef;
  if (typeof header === 'string') {
    return header;
  }

  if (typeof header === 'function') {
    return header({} as any);
  }

  return column.columnDef.id;
};

export const ColumnItem: React.FC<ColumnItemProps> = ({ column }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<ColumnState>(idle);

  useEffect(() => {
    const element = ref.current as any;
    return combine(
      draggable({
        element,
        getInitialData() {
          return getColumnData(column);
        },
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: '16px',
              y: '8px',
            }),
            render({ container }) {
              setState({ type: 'preview', container });
            },
          });
        },
        onDragStart() {
          setState({ type: 'is-dragging' });
        },
        onDrop() {
          setState(idle);
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          // not allowing dropping on yourself
          if (source.element === element) {
            return false;
          }
          // only allowing columns to be dropped on me
          return isColumnData(source.data);
        },
        getData({ input }) {
          const data = getColumnData(column);
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          });
        },
        getIsSticky() {
          return true;
        },
        onDragEnter({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState({ type: 'is-dragging-over', closestEdge });
        },
        onDrag({ self }) {
          const closestEdge = extractClosestEdge(self.data);

          // Only need to update react state if nothing has changed.
          // Prevents re-rendering.
          setState(current => {
            if (
              current.type === 'is-dragging-over' &&
              current.closestEdge === closestEdge
            ) {
              return current;
            }
            return { type: 'is-dragging-over', closestEdge };
          });
        },
        onDragLeave() {
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      }),
    );
  }, [column]);

  return (
    <>
      <Box pos="relative">
        <Box ref={ref} py={8}>
          <Flex align="center">
            <Flex align="center" sx={{ cursor: 'grab' }}>
              <DragDropIcon />
            </Flex>
            <Checkbox
              mb={0}
              mr={8}
              disabled={column.columnDef.meta?.disableVisibilityManagement}
              checked={column.getIsVisible()}
              onChange={column.getToggleVisibilityHandler()}
              onClick={e => e.stopPropagation()}
            />
            <Box>{getLabel(column)}</Box>
          </Flex>
        </Box>
        {state.type === 'is-dragging-over' && state.closestEdge ? (
          <DropIndicator gap={8} edge={state.closestEdge} />
        ) : null}
      </Box>
      {state.type === 'preview'
        ? createPortal(<DragPreview column={column} />, state.container)
        : null}
    </>
  );
};

// A simplified version of our column for the user to drag around
const DragPreview: React.FC<{ column: Column<any, unknown> }> = ({
  column,
}) => <div>{getLabel(column)}</div>;
